import { useWizard } from '@/components/common/wizard/WizardContext'
import { memo, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_AUDIENCES } from '@/graphql/audience-queries'
import { useAudienceDataWithHypotheses } from '@/hooks/useAudiencesWithHypotheses'
import { getAllStages, getAvailableStages } from '@/utils/ad-campaigns-helpers'
import AudienceStageItem from './AudienceStageItem'
import { CubeLoader } from '@/components/ui/cube-loader'
import { RadioGroup } from '@/components/ui/radio-group'
import useAdsSelectionStore from '@/store/adsSelectionStore'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

const StageHeader = ({ stage, collapsedStages, toggleStageCollapse, totalCreatives, availableStages }) => {
    const isDisabled = !availableStages.includes(stage.id)

    return (
        <div className="tw:inline-flex tw:gap-2 tw:items-center tw:px-6">
            {/* Collapse/Expand Icon */}
            <button
                onClick={() => toggleStageCollapse(stage.id)}
                className="tw:flex tw:items-center tw:justify-center hover:tw:bg-gray-700 tw:rounded tw:transition-colors"
                type="button"
            >
                <ChevronDown
                    className={cn(
                        'tw:size-4 tw:cursor-pointer tw:text-blue-300 tw:transition-transform tw:duration-300',
                        isDisabled && 'tw:hidden',
                        collapsedStages.has(stage.id) && 'tw:rotate-180',
                    )}
                />
            </button>

            {isDisabled && <div className="tw:size-3 tw:rounded-full tw:border-2"></div>}
            {!isDisabled && <div className="tw:size-3 tw:rounded-full" style={{ backgroundColor: stage.color }} />}

            <span className="tw:font-semibold tw:text-white">{stage.name}</span>
        </div>
    )
}

const AudienceStagesList = memo(
    ({
        audienceSelections,
        onEditAudience,
        onCreativeRemove,
        getTotalCreativesForStage,
        getSelectedCreativesForAudience,
    }) => {
        const { getStepContentData } = useWizard()
        const { data: catalogueAudiencesData, loading: audiencesLoading } = useQuery(GET_AUDIENCES)
        const audiencesData = useAudienceDataWithHypotheses(catalogueAudiencesData, getStepContentData)
        const [collapsedStages, setCollapsedStages] = useState(new Set())
        const selectFormatData = getStepContentData('campaignObjective') || {}
        const selectedObjective = selectFormatData.selectedObjective?.toLowerCase() ?? 'awareness'
        const availableStages = getAvailableStages(selectedObjective)

        // Get store methods and state
        const {
            selectedAudience,
            selectedStage,
            setSelectedAudience: setStoreSelectedAudience,
        } = useAdsSelectionStore()

        // Build the radio group value from store state
        const selectedRadioValue = selectedAudience && selectedStage ? `${selectedStage.id}_${selectedAudience.id}` : ''

        const audiences = useMemo(() => {
            return audiencesData?.Audience?.audiences || []
        }, [audiencesData])

        const allStages = useMemo(() => getAllStages(), [])

        // Get selected audiences for a stage
        const getSelectedAudiences = (stageId) => {
            const selectedIds = audienceSelections[stageId] || []
            return audiences.filter((audience) => selectedIds.includes(audience.id))
        }

        // Get selected creatives with metadata for display
        const getSelectedCreativesWithMetadata = (audienceId, stageId) => {
            const creativeIds = getSelectedCreativesForAudience(audienceId, stageId)
            // For now, return mock data structure. In a real implementation,
            // you'd fetch creative metadata from the store or cache
            return creativeIds.map((id) => ({
                id,
                name: `Creative ${id}`,
                thumbnail: null, // Would be populated with actual thumbnail URLs
            }))
        }

        // Handle radio group selection
        const handleAudienceSelection = (stageAudienceId) => {
            const [stageId, audienceId] = stageAudienceId.split('_')
            const audience = audiences.find((a) => a.id === audienceId)
            const stage = allStages.find((s) => s.id === stageId)

            if (audience && stage) {
                // Update store with selected audience/stage
                setStoreSelectedAudience(audience, stage)
                // Also call parent callback to maintain compatibility
                onEditAudience(audience, stage)
            }
        }

        // Handle stage collapse/expand
        const toggleStageCollapse = (stageId) => {
            setCollapsedStages((prev) => {
                const newSet = new Set(prev)
                if (newSet.has(stageId)) {
                    newSet.delete(stageId)
                } else {
                    newSet.add(stageId)
                }
                return newSet
            })
        }

        // Get visible stages (stages that have selected audiences)
        const visibleStageIds = useMemo(() => {
            return allStages.filter((stage) => getSelectedAudiences(stage.id).length > 0).map((stage) => stage.id)
        }, [allStages, audienceSelections, audiences])

        // Check if all visible stages are collapsed
        const areAllStagesCollapsed = useMemo(() => {
            return visibleStageIds.length > 0 && visibleStageIds.every((stageId) => collapsedStages.has(stageId))
        }, [visibleStageIds, collapsedStages])

        // Handle toggle all stages (collapse all or expand all)
        const toggleAllStages = () => {
            if (areAllStagesCollapsed) {
                // Expand all - clear collapsed stages
                setCollapsedStages(new Set())
            } else {
                // Collapse all - add all visible stages to collapsed set
                setCollapsedStages(new Set(visibleStageIds))
            }
        }

        if (audiencesLoading) {
            return (
                <div className="tw:flex tw:justify-center tw:items-center tw:py-12">
                    <CubeLoader className="tw:w-8 tw:h-8" size="default" />
                </div>
            )
        }

        return (
            <RadioGroup value={selectedRadioValue} onValueChange={handleAudienceSelection}>
                <div className="tw:mt-6 tw:flex tw:gap-6 tw:flex-col">
                    {allStages.map((stage, index) => {
                        const selectedAudiences = getSelectedAudiences(stage.id)
                        const totalCreatives = getTotalCreativesForStage(stage.id)
                        const isFirstStage = index === 0

                        if (selectedAudiences.length === 0) return null

                        return (
                            <div key={stage.id} className="tw:flex tw:gap-4 tw:flex-col">
                                {/* Stage Header */}
                                <StageHeader
                                    stage={stage}
                                    collapsedStages={collapsedStages}
                                    toggleStageCollapse={toggleStageCollapse}
                                    totalCreatives={totalCreatives}
                                    availableStages={availableStages}
                                />

                                {/* Audience Items - only show if not collapsed */}
                                {!collapsedStages.has(stage.id) && availableStages.includes(stage.id) && (
                                    <div className="tw:flex tw:flex-col tw:border-t tw:border-b tw:divide-y tw:divide-gray-600 tw:border-gray-600">
                                        {selectedAudiences.map((audience) => (
                                            <AudienceStageItem
                                                key={`${stage.id}_${audience.id}`}
                                                audience={audience}
                                                stage={stage}
                                                stageAudienceId={`${stage.id}_${audience.id}`}
                                                onEdit={onEditAudience}
                                                onCreativeRemove={onCreativeRemove}
                                                selectedCreatives={getSelectedCreativesWithMetadata(
                                                    audience.id,
                                                    stage.id,
                                                )}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </RadioGroup>
        )
    },
)

AudienceStagesList.displayName = 'AudienceStagesList'

export default AudienceStagesList
